import { Injectable } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';

import { ServiceResponse } from "@common/data-models/service/service-response";
import { AssignmentRecord } from '@playbook/data-models/records/assignment-record';
import { TopicRecord } from "@playbook/data-models/records/topic-record";
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ApiServiceCollectionRecord } from '@common/data-models/service/api-service-collection-record';
import { ApiServiceRecord } from '@common/data-models/service/api-service-record';
import { FilterDef } from "@common/data-models/navigation/filter-def";
import { ObjectTypes } from "@common/enums/object-types";
import { ViewStateRecordsAlias } from "@common/services/layout-state";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { DataStore } from "@common/data-models/data-store/data-store";
import { ViewStateTypes } from "@common/enums/viewstate-types";
import { ViewStateOptions } from "@suite/common/data-models/navigation/viewstate-options";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { ZFields } from "@common/data-models/fields/z-fields";
import { PlatformConvertor } from "@common/services/platform-convertor";
import { CastSearchResults } from "@cast/data-models/cast-search-results/cast-search-results";
import { Record } from '@common/data-models/records/record'
import { PublicationRecord } from '../data-models/records/publication-record';
import { PublicationChannelRecord, PublicationChannelSchedule } from '@suite/common/data-models/records/publication-channel-record';
import { Relationship } from '@common/data-models/complex-types/relationship';
import { PublicationListRecord } from '../data-models/records/publication-list-record';
import { PublicationViewStateRecord } from '@suite/common/data-models/records/publication-viewstate-record';
import { DateRange } from '@suite/common/data-models/complex-types/date-range';
import { DateTime } from '@suite/common/data-models/complex-types/date-time';
import { RecordFactory } from '@suite/common/data-models/records/record-factory';
import moment from 'moment-timezone';

@Injectable({ providedIn: 'root' })
export class CastSearchService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public platformConvertor: PlatformConvertor
    ) { }

    public async getPublicationListsOfRestrictedChannelType(vs: ViewStateRecordsAlias, channelsGuids: Array<string>): Promise<CastSearchResults> {
        let results = new CastSearchResults();
        let response: HttpResponse<any> = null;
        let recordRequestParams = this.buildSearchParamsForPublicationListsOfRestrictedChannelType(vs, channelsGuids);
        recordRequestParams.IncludeChildren = true;

        recordRequestParams["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];
        try {
            response = await this.nomWebServicesJson.post(
                "/api/v2/Search",
                recordRequestParams, {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load search results!",
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`
            });
            return null;
        }
            let i = 0;
        if (response) {
            let respObj = response.body;
            for (let p of respObj.SearchResults) {
                if (p.ListData.Type == ObjectTypes.PUBLICATIONLIST) {
                    let serviceRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                    serviceRecord.instantiateChildren();
                    let publicationListRecord = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord<PublicationListRecord>(serviceRecord);
                    results.publicationListsOfRestrictedChannelTypes.push(publicationListRecord);
                    DataStore.set(publicationListRecord);
                }
            }
            results.objectType = ObjectTypes.PUBLICATIONLIST;
            return results;
        }
    }

    public async getSearchResults(vs: ViewStateRecordsAlias): Promise<ServiceResponse<CastSearchResults>> {
        //1. we need to get the channel guid from vs
        //2. load publication list and channel data based on channel guid and current date. take latest revision (unpublished)
        //3. load pub items
        //4. divide them across sections based on pub list section field (JSON payload)
        // channel service ?
        // navigate prev and next => filter by pub list with that channel guid and that time range
        // revision dropdown
        // PUBLISH button => mark current pub list as published and clear area
        let results = new CastSearchResults();
        if (!(<PublicationViewStateRecord>vs).publicationChannel && !(<PublicationViewStateRecord>vs).publicationChannel.channelSchedule) {
            // nothing to do
            return new ServiceResponse<CastSearchResults>(false, results);
        }
        let response: HttpResponse<any> = null;
        let recordRequestParams = this.buildSearchParams(vs);
        recordRequestParams.IncludeChildren = true;

        recordRequestParams["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];
        try {
            response = await this.nomWebServicesJson.post(
                "/api/v2/Search",
                recordRequestParams, {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load search results!",
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`
            });
            return new ServiceResponse<CastSearchResults>(false, results);
        }
        if (response) {
            let respObj = response.body;
            for (let p of respObj.SearchResults) {
                if (p.ListData.Type == ObjectTypes.PUBLICATIONLIST) {
                    let serviceRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                    serviceRecord.instantiateChildren();
                    let publicationListRecord = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord<PublicationListRecord>(serviceRecord);
                    await DataStore.setCollection(publicationListRecord, true);
                    results.publicationList = DataStore.byGuid<PublicationListRecord>(publicationListRecord.guid, "");
                    break; // only process ONE publication list
                }
            }
            if (results.publicationList != null) {
                let linkedObjects: Array<string> = new Array<string>();
                // now we need to fetch linked records, if they don't already exist in the data store
                for (let i = 0; i < results.publicationList.getChildren().length; i++) {
                    let publication: PublicationRecord = <PublicationRecord>results.publicationList.getChildren()[i];
                    if (publication.relationships && publication.relationships.getRowCount() > 0) {
                        linkedObjects.push(publication.relationships.getGuid(0));
                    }
                }
                if (linkedObjects.length > 0) {
                    // now fetch linked guids
                    let linkedRequestParams = this.buildSequenceParams(linkedObjects);
                    linkedRequestParams["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];
                    let linkedResponse: HttpResponse<any> = null;
                    try {
                        linkedResponse = await this.nomWebServicesJson.post(
                            "/api/v2/Search",
                            linkedRequestParams, {
                                headers: new HttpHeaders({
                                    "Content-Type": "application/json",
                                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                                }),
                                responseType: 'json',
                                observe: 'response'
                            });
                    } catch (error) {
                        this.nomWebServicesJson.showErrorModal({
                            message: "Failed to load search results!",
                            detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`
                        });
                        return new ServiceResponse<CastSearchResults>(false, results);
                    }
                    if (linkedResponse) {
                        let linkedRespObj = linkedResponse.body;
                        let i = 0;
                        for (let p of linkedRespObj.SearchResults) {
                            i++;
                            if ((i == linkedRespObj.SearchResults.length && i > linkedRespObj.MaxResultsReturned)) continue;
                            if (p.ListData.Type == 137) {
                                //1. add Topic results
                                let serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                                let topicRecord = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord<TopicRecord>(serviceCollectionRecord);
                                await DataStore.setCollection(topicRecord, true);
                                let record = DataStore.byGuid<TopicRecord>(topicRecord.guid, "");
                                record.setChildren([]);
                                for (let childScript of p.CollectionScripts) {
                                    let assignmentRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<AssignmentRecord>(childScript);
                                    DataStore.set(assignmentRecord);
                                    let assignment = DataStore.byGuid<AssignmentRecord>(assignmentRecord.guid, "");
                                    if (record && !record.hasChildWithGuid(assignment.guid)) {
                                        record.unshiftChildren(assignment);
                                    } else {
                                        record.registerChildHooks(assignment);
                                    }
                                }
                            }
                            if (p.ListData.Type == 138) {
                                //2. assignment results - that may or may not have topics
                                let serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                                let assignmentRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<AssignmentRecord>(serviceRecord);
                                let parentRec: TopicRecord = DataStore.byGuid<TopicRecord>(assignmentRecord.parentGuid, "");
                                if (!parentRec) {
                                    parentRec = RecordFactory.createRecordForType<TopicRecord>(ObjectTypes.TOPIC)
                                }
                                parentRec.guid = assignmentRecord.parentGuid;
                                parentRec.categories = assignmentRecord.parentCategory;
                                parentRec.topicType = assignmentRecord.parentType;
                                parentRec.storyOwner = assignmentRecord.parentOwner;
                                parentRec.location = assignmentRecord.parentLocation;
                                parentRec.title = p.ListData.ContainerTitle;
                                await DataStore.setCollection(parentRec, true);
                                if (parentRec && !parentRec.hasChildWithGuid(assignmentRecord.guid)) {
                                    parentRec.unshiftChildren(assignmentRecord);
                                } else {
                                    parentRec.registerChildHooks(assignmentRecord);
                                }
                                DataStore.set(assignmentRecord);
                            }
                        }
                    }
                }
            }
        }
        results.objectType = ObjectTypes.PUBLICATION;
        return new ServiceResponse<CastSearchResults>(true, results);
    }

    private getObjectTypeByViewStateType(vsType: ViewStateTypes, guid?: string) {
        return ObjectTypes.PUBLICATION;
    }

    private buildGenericSearchReqParams(maxRows: number = 200, includeChildren: boolean = false): any {
        maxRows = (maxRows < 200) ? 200 : maxRows;
        return ({
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: "",
            zFields: null,
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: "1800-02-28T22:00:00.000Z",
                ItemDateTo: "1800-02-28T22:00:00.000Z",
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: "1800-02-28T22:00:00.000Z",
                    ItemDateTo: "1800-02-28T22:00:00.000Z",
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false
                },
                ExtendedWireBriefing: true
            },
            MaxRows: maxRows,
            SortByRank: false,
            SortFields: null,
            ExactMatch: false,
            IncludeChildren: includeChildren
        });
    }

    public calculateNgTimeOffset(date: Date) {
        const offset = this.visualStateService.ConfigSettings.NGTimeOffset;
        if (offset) {
            const chunks = offset.split(':');
            const hours = parseInt(chunks[0]);
            const minutes = parseInt(chunks[1]);
            date.setHours(hours);
            date.setMinutes(minutes);
        } else {
            date.setHours(12);
            date.setMinutes(0);
        }
    }

    protected buildSearchParams(vs: ViewStateRecordsAlias): any {
        let zFields = new Array<any>();
            zFields.push({
                "FieldName": "PSTPublicationChannelGUID",
                "FieldValue": (<PublicationViewStateRecord>vs).publicationChannelGUID
            });
            let range: DateRange = new DateRange();
            let time: DateTime = (<PublicationViewStateRecord>vs).publicationTime;
            if (!time || !time.get()) {
                let dt = new DateTime();
                let d = new Date();
                d.setHours(0, 0, 0, 0);
                dt.set(d);
                (<PublicationViewStateRecord>vs).publicationTime = dt;
                time = dt;
            }
            this.calculateNgTimeOffset(time.get());
            let channelRange: PublicationChannelSchedule = (<PublicationViewStateRecord>vs).publicationChannel.channelSchedule;
            if (channelRange.toUpperCase() == "DAILY") {
                range.from = moment(time.get()).subtract(2, 'hours').toDate();
                range.to = moment(time.get()).add(2, 'hours').toDate();
            } else {
                // not supported for now
                throw new Error("Range " + channelRange + " is not supported.");
            }
            // construct actual search range based on current time in UI as well as Channel's range value, which can be: daily, hourly, weekly, monthly, etc.
            let rangeVal: string = moment(range.from).format("YYYY-MM-DDTHH:mm:ss.000") + "Z" + ";" + moment(range.to).format("YYYY-MM-DDTHH:mm:ss.000") + "Z";

            // construct date range zfield filter
            zFields.push({
                "FieldName": "PSTPublicationListTime__range__",
                "FieldValue": rangeVal
            });

            zFields.push({
                "FieldName": "PSTPublicationEditionName",
                "FieldValue": (<PublicationViewStateRecord>vs).editionName
            });
            // construct revision. default (if not set) should be max => latest rev. that's why we are sorting by desc. also, in this case
            // we should only return 1 list
            let params = this.buildGenericSearchReqParams();
            if ((<PublicationViewStateRecord>vs).publicationRevisionNumber >= 0) {
                zFields.push({
                    "FieldName": "PSTPublicationListRevisionNumber",
                    "FieldValue": (<PublicationViewStateRecord>vs).publicationRevisionNumber
                });
            } else {
                params.SortFields = [{ FieldName: "PSTPublicationListRevisionNumber", FieldValue: "desc" }];
            }
        params.MaxRows = 1;
        params.MaxChildRows = 200;
        params.zFields = zFields;
        return params;
    }

    protected buildSequenceParams(sequence: Array<string>): any {
        let zFields = new Array<any>();
        zFields.push({
            "FieldName": "guid__list__",
            "FieldValue": sequence.join(";")
        });
        let params = this.buildGenericSearchReqParams(sequence.length, false);
        params.zFields = zFields;
        return params;
    }

    protected buildSearchParamsForPublicationListsOfRestrictedChannelType(vs: ViewStateRecordsAlias, channelsGuids: Array<string>): any {
        let zFields = new Array<any>();
        zFields.push({
            "FieldName": "PSTPublicationChannelGUID__list__",
            "FieldValue": channelsGuids.join(";")
        });
        zFields.push({
            "FieldName": "type",
            "FieldValue": ObjectTypes.PUBLICATIONLIST
        });
        let params = this.buildGenericSearchReqParams();
        params.zFields = zFields;
        return params;
    }
}
